import { useCallback, useEffect, useEffectEvent, useState } from "react"
import {
  type BetterDocUsageEvaluation,
  fetchBetterDocUsageForConsultation,
} from "@/modules/patients/detail/better-doc/lib/fetch-better-doc-usage-for-consultation"
import type { PointOfCareWithBetterDoc } from "@/modules/patients/detail/better-doc/types"
import { BetterDoc, BetterDocConsultationDetail, Country } from "@/types/__generated__/union-types"

export type BetterDocUsageEvaluationOutcome =
  | {
      status: "EVALUATED"
      betterDocUsageEvaluation: BetterDocUsageEvaluation
      previousSuccessfulEvaluation: BetterDocUsageEvaluation | null
    }
  | { status: "FAILED" }

type UseBetterDocUsageForConsultationParameters = {
  country: Country | undefined
  patientId: string
  diagnosisCode: string | null
  effectivePointOfCare: PointOfCareWithBetterDoc | null
  specialtyId: string | null
  enabled: boolean
  onBetterDocUsageEvaluated: (betterDocUsageEvaluationOutcome: BetterDocUsageEvaluationOutcome) => void
}

export type BetterDocUsageForConsultationResult = {
  betterDocUsage: BetterDoc | null
  missingConsultationDetailsThatCanChangeBetterDocUsage: ReadonlyArray<BetterDocConsultationDetail>
  isBetterDocUsagePending: boolean
  shouldShowBetterDocUsageFailure: boolean
  dismissBetterDocUsageFailure: () => void
}

type BetterDocUsageEvaluationState =
  | { status: "PENDING" }
  | { status: "EVALUATED"; betterDocUsageEvaluation: BetterDocUsageEvaluation }
  | { status: "FAILED"; isFailureDismissed: boolean }

type BetterDocUsageEvaluationForPointOfCare = {
  evaluatedPointOfCare: PointOfCareWithBetterDoc | null
  lastSuccessfulEvaluation: BetterDocUsageEvaluation | null
  evaluation: BetterDocUsageEvaluationState
}

const PENDING_EVALUATION: BetterDocUsageEvaluationState = { status: "PENDING" }

const FAILED_EVALUATION: BetterDocUsageEvaluationState = { status: "FAILED", isFailureDismissed: false }

const DISMISSED_FAILED_EVALUATION: BetterDocUsageEvaluationState = { status: "FAILED", isFailureDismissed: true }

const NOT_EVALUATED: Pick<
  BetterDocUsageForConsultationResult,
  "betterDocUsage" | "missingConsultationDetailsThatCanChangeBetterDocUsage"
> = {
  betterDocUsage: null,
  missingConsultationDetailsThatCanChangeBetterDocUsage: [],
}

const createPendingEvaluationForPointOfCare = (
  evaluatedPointOfCare: PointOfCareWithBetterDoc | null,
): BetterDocUsageEvaluationForPointOfCare => ({
  evaluatedPointOfCare,
  lastSuccessfulEvaluation: null,
  evaluation: PENDING_EVALUATION,
})

export const useBetterDocUsageForConsultation = ({
  country,
  patientId,
  diagnosisCode,
  effectivePointOfCare,
  specialtyId,
  enabled,
  onBetterDocUsageEvaluated,
}: UseBetterDocUsageForConsultationParameters): BetterDocUsageForConsultationResult => {
  const [evaluationForPointOfCare, setEvaluationForPointOfCare] = useState<BetterDocUsageEvaluationForPointOfCare>(() =>
    createPendingEvaluationForPointOfCare(effectivePointOfCare),
  )

  if (evaluationForPointOfCare.evaluatedPointOfCare !== effectivePointOfCare) {
    setEvaluationForPointOfCare(createPendingEvaluationForPointOfCare(effectivePointOfCare))
  }

  const reportBetterDocUsageEvaluation = useEffectEvent(
    (betterDocUsageEvaluation: BetterDocUsageEvaluation | undefined): void => {
      if (!betterDocUsageEvaluation) {
        setEvaluationForPointOfCare((currentEvaluation) => ({ ...currentEvaluation, evaluation: FAILED_EVALUATION }))
        onBetterDocUsageEvaluated({ status: "FAILED" })
        return
      }

      const previousSuccessfulEvaluation = evaluationForPointOfCare.lastSuccessfulEvaluation
      setEvaluationForPointOfCare((currentEvaluation) => ({
        ...currentEvaluation,
        lastSuccessfulEvaluation: betterDocUsageEvaluation,
        evaluation: { status: "EVALUATED", betterDocUsageEvaluation },
      }))
      onBetterDocUsageEvaluated({ status: "EVALUATED", betterDocUsageEvaluation, previousSuccessfulEvaluation })
    },
  )

  useEffect(() => {
    if (!enabled || !country || !patientId || !effectivePointOfCare) {
      return
    }

    let isLatestEvaluation = true

    fetchBetterDocUsageForConsultation(country, {
      patientId,
      diagnosisCode,
      effectivePointOfCare,
      specialtyId,
    }).then(
      (betterDocUsageEvaluation) => {
        if (isLatestEvaluation) {
          reportBetterDocUsageEvaluation(betterDocUsageEvaluation)
        }
      },
      () => {
        if (isLatestEvaluation) {
          reportBetterDocUsageEvaluation(undefined)
        }
      },
    )

    return () => {
      isLatestEvaluation = false
    }
  }, [country, enabled, patientId, diagnosisCode, effectivePointOfCare, specialtyId])

  const dismissBetterDocUsageFailure = useCallback(() => {
    setEvaluationForPointOfCare((currentEvaluation) =>
      currentEvaluation.evaluation.status === "FAILED"
        ? { ...currentEvaluation, evaluation: DISMISSED_FAILED_EVALUATION }
        : currentEvaluation,
    )
  }, [])

  const { evaluation } = evaluationForPointOfCare
  const betterDocUsageEvaluation =
    enabled && evaluation.status === "EVALUATED" ? evaluation.betterDocUsageEvaluation : NOT_EVALUATED

  return {
    ...betterDocUsageEvaluation,
    isBetterDocUsagePending: enabled && evaluation.status === "PENDING",
    shouldShowBetterDocUsageFailure: enabled && evaluation.status === "FAILED" && !evaluation.isFailureDismissed,
    dismissBetterDocUsageFailure,
  }
}
